import React, { useState, useEffect, useCallback, useRef } from 'react';
import apiService from '../services/api';
import Sparkline from './Sparkline';
import { getChartTrendColor, getQuoteChangeColor } from '../utils/marketChart';

// 3 tabs: US, China, Crypto
const MARKET_TABS = {
    US: 'SPY,QQQ,DIA,SOXX,NVDA,AAPL,TSLA',
    China: 'FXI,KWEB,MCHI,ASHR',
    Crypto: 'BINANCE:BTCUSDT,BINANCE:ETHUSDT,BINANCE:SOLUSDT,BINANCE:TRUMPUSDT,BINANCE:WLDUSDT',
};

const CACHE_TTL = 60_000; // 1 minute

const PERIOD_OPTIONS = ['1D', '5D', '1M', '3M', '6M', '1Y'];
const PERIOD_API_MAP = { '1D': '1d', '5D': '5d', '1M': '1mo', '3M': '3mo', '6M': '6mo', '1Y': '1y' };

const MarketOverview = () => {
    const [activeTab, setActiveTab] = useState('US');
    const [quotes, setQuotes] = useState([]);
    const [selectedSymbol, setSelectedSymbol] = useState(null);
    const [chartData, setChartData] = useState(null);
    const [chartPeriod, setChartPeriod] = useState('1D');
    const [loading, setLoading] = useState(true);
    const [chartLoading, setChartLoading] = useState(false);

    const quotesCache = useRef({});
    const chartCache = useRef({});

    const fetchQuotes = useCallback(async () => {
        const cached = quotesCache.current[activeTab];
        if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
            setQuotes(cached.data);
            if (cached.data.length > 0 && !cached.data[0].error) {
                setSelectedSymbol(prev => {
                    const tabSymbols = MARKET_TABS[activeTab].split(',');
                    return prev && tabSymbols.includes(prev) ? prev : cached.data[0].symbol;
                });
            }
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const symbols = MARKET_TABS[activeTab];
            const res = await apiService.getMarketSnapshot(symbols);
            const data = res?.data || [];
            const list = Array.isArray(data) ? data : [];
            setQuotes(list);
            quotesCache.current[activeTab] = { data: list, timestamp: Date.now() };
            if (list.length > 0 && !list[0].error) {
                setSelectedSymbol(list[0].symbol);
            }
        } catch (err) {
            console.error('Failed to load market data:', err);
            // Keep existing quotes on error instead of clearing
        } finally {
            setLoading(false);
        }
    }, [activeTab]);

    const fetchChart = useCallback(async () => {
        if (!selectedSymbol) return;

        const cacheKey = `${selectedSymbol}|${chartPeriod}`;
        const cached = chartCache.current[cacheKey];
        if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
            setChartData(cached.data);
            return;
        }

        setChartLoading(true);
        setChartData(null);
        try {
            const period = PERIOD_API_MAP[chartPeriod] || '1d';
            const res = await apiService.getMarketChart(selectedSymbol, period);
            const data = res?.prices ? res : (res?.data || null);
            // Only update chart if we got valid prices; keep stale data on rate-limit/error
            if (data?.prices?.length > 0) {
                setChartData(data);
                chartCache.current[cacheKey] = { data, timestamp: Date.now() };
            } else {
                setChartData(data);
            }
        } catch (err) {
            console.error('Failed to load chart:', err);
            setChartData(null);
        } finally {
            setChartLoading(false);
        }
    }, [selectedSymbol, chartPeriod]);

    useEffect(() => { fetchQuotes(); }, [fetchQuotes]);
    useEffect(() => { fetchChart(); }, [fetchChart]);

    useEffect(() => {
        const timer = setInterval(() => {
            delete quotesCache.current[activeTab];
            fetchQuotes();
        }, CACHE_TTL);
        return () => clearInterval(timer);
    }, [activeTab, fetchQuotes]);

    const selectedQuote = quotes.find(q => q.symbol === selectedSymbol);
    const quoteColor = getQuoteChangeColor(selectedQuote?.change_percent);
    const chartColor = getChartTrendColor(chartData?.prices, selectedQuote?.change_percent);
    const showChart = !!selectedSymbol;

    return (
        <div style={styles.container}>
            {/* Top bar: title + tabs + period selectors */}
            <div style={styles.topBar}>
                <div style={styles.tabRow}>
                    {Object.keys(MARKET_TABS).map((tab, idx) => (
                        <React.Fragment key={tab}>
                            {idx > 0 && <span style={styles.tabDivider}>|</span>}
                            <button
                                onClick={() => setActiveTab(tab)}
                                style={{
                                    ...styles.tabBtn,
                                    color: activeTab === tab ? '#1D1D1F' : '#86868B',
                                    fontWeight: activeTab === tab ? '700' : '500',
                                }}
                            >
                                {tab}
                            </button>
                        </React.Fragment>
                    ))}
                </div>
                {showChart && (
                    <div style={styles.periodRow}>
                        {PERIOD_OPTIONS.map(p => (
                            <button
                                key={p}
                                onClick={() => setChartPeriod(p)}
                                style={{
                                    ...styles.periodBtn,
                                    backgroundColor: chartPeriod === p ? '#1D1D1F' : 'transparent',
                                    color: chartPeriod === p ? '#fff' : '#86868B',
                                }}
                            >
                                {p}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* Main content: table left + chart right */}
            <div style={styles.body}>
                {/* Left: Stock table */}
                <div style={styles.tableSection}>
                    {loading ? (
                        <div style={styles.placeholder}>Loading...</div>
                    ) : quotes.length === 0 ? (
                        <div style={styles.placeholder}>No data available</div>
                    ) : (
                        quotes.map(q => {
                            if (q.error) return null;
                            const isUp = q.change_percent >= 0;
                            const color = isUp ? '#34C759' : '#FF3B30';
                            const isSelected = q.symbol === selectedSymbol;
                            return (
                                <div
                                    key={q.symbol}
                                    style={{
                                        ...styles.row,
                                        borderLeftColor: isSelected ? color : 'transparent',
                                        backgroundColor: isSelected ? 'rgba(0,0,0,0.02)' : 'transparent',
                                    }}
                                    onClick={() => setSelectedSymbol(q.symbol)}
                                >
                                    <div style={styles.rowName}>
                                        {typeof q.name === 'string' ? q.name : q.symbol}
                                    </div>
                                    <div style={styles.rowPrice}>
                                        {q.price != null ? q.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '--'}
                                    </div>
                                    <div style={{ ...styles.rowChange, color }}>
                                        {q.change != null ? `${isUp ? '+' : ''}${q.change.toFixed(2)}` : '--'}
                                    </div>
                                    <div style={{ ...styles.rowPct, color }}>
                                        {q.change_percent != null ? `${isUp ? '+' : ''}${q.change_percent.toFixed(2)}%` : '--'}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>

                {/* Right: Chart */}
                {showChart && (
                    <div style={styles.chartSection}>
                        <div style={styles.chartHeader}>
                            <span style={styles.chartSymbol}>
                                {typeof selectedQuote?.name === 'string' ? selectedQuote.name : selectedSymbol}
                            </span>
                            {selectedQuote?.price != null && (
                                <span style={styles.chartPrice}>
                                    {selectedQuote.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </span>
                            )}
                            {selectedQuote?.change_percent != null && (
                                <span style={{ ...styles.chartPct, color: quoteColor }}>
                                    {selectedQuote.change_percent >= 0 ? '+' : ''}{selectedQuote.change_percent.toFixed(2)}%
                                </span>
                            )}
                        </div>
                        <div style={styles.chartBody}>
                            {chartLoading ? (
                                <div style={{ height: 140, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#86868B', fontSize: '0.8rem' }}>
                                    Loading chart...
                                </div>
                            ) : (
                                <Sparkline
                                    prices={chartData?.prices}
                                    color={chartColor}
                                    height={140}
                                />
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const styles = {
    container: {
        backgroundColor: 'rgba(255, 255, 255, 0.7)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderRadius: '24px',
        border: '1px solid rgba(255, 255, 255, 0.5)',
        boxShadow: '0 4px 24px rgba(0, 0, 0, 0.04)',
        padding: '1rem 1.25rem',
    },
    topBar: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingBottom: '10px',
        borderBottom: '1px solid rgba(0,0,0,0.08)',
        marginBottom: '0',
    },
    tabRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '2px',
    },
    tabBtn: {
        background: 'none',
        border: 'none',
        padding: '4px 10px',
        fontSize: '0.82rem',
        cursor: 'pointer',
        transition: 'color 0.15s ease',
    },
    tabDivider: {
        color: '#D1D1D6',
        fontSize: '0.75rem',
        userSelect: 'none',
    },
    periodRow: {
        display: 'flex',
        gap: '2px',
    },
    periodBtn: {
        padding: '3px 8px',
        borderRadius: '5px',
        border: 'none',
        fontSize: '0.7rem',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.15s ease',
    },
    body: {
        display: 'flex',
        gap: '0',
    },
    tableSection: {
        flex: '0 0 55%',
        borderRight: '1px solid rgba(0,0,0,0.06)',
    },
    row: {
        display: 'grid',
        gridTemplateColumns: '1.4fr 1fr 0.8fr 0.8fr',
        alignItems: 'center',
        padding: '6px 10px 6px 12px',
        borderBottom: '1px solid rgba(0,0,0,0.04)',
        borderLeft: '3px solid transparent',
        cursor: 'pointer',
        transition: 'background-color 0.1s ease',
        fontSize: '0.85rem',
    },
    rowName: {
        fontWeight: '600',
        color: '#1D1D1F',
        fontSize: '0.84rem',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    rowPrice: {
        textAlign: 'right',
        fontWeight: '700',
        fontVariantNumeric: 'tabular-nums',
        letterSpacing: '-0.01em',
    },
    rowChange: {
        textAlign: 'right',
        fontWeight: '600',
        fontVariantNumeric: 'tabular-nums',
        fontSize: '0.83rem',
    },
    rowPct: {
        textAlign: 'right',
        fontWeight: '700',
        fontVariantNumeric: 'tabular-nums',
        fontSize: '0.83rem',
    },
    placeholder: {
        textAlign: 'center',
        color: '#86868B',
        padding: '2rem 0',
        fontSize: '0.85rem',
    },
    chartSection: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        paddingLeft: '1rem',
        minWidth: 0,
    },
    chartHeader: {
        display: 'flex',
        alignItems: 'baseline',
        gap: '8px',
        padding: '6px 0 4px',
    },
    chartSymbol: {
        fontSize: '0.88rem',
        fontWeight: '700',
        color: '#1D1D1F',
    },
    chartPrice: {
        fontSize: '0.88rem',
        fontWeight: '700',
        color: '#1D1D1F',
    },
    chartPct: {
        fontSize: '0.8rem',
        fontWeight: '700',
    },
    chartBody: {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
    },
};

export default MarketOverview;
